import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { BellIcon } from '@heroicons/react/24/outline'
import { 
  BellIcon as BellIconSolid, 
  UserPlusIcon, 
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  UserCircleIcon
} from '@heroicons/react/24/solid'

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [userInfo, setUserInfo] = useState(null)

  useEffect(() => {
    fetchUserInfo()
  }, [])

  useEffect(() => {
    if (userInfo) {
      fetchNotifications()
      
      // Set up real-time subscription for notifications table
      const channel = supabase
        .channel('notifications-subscription')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${userInfo.id}`
          },
          (payload) => {
            fetchNotifications()
          }
        )
        .subscribe()

      return () => {
        supabase.removeChannel(channel)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo])

  const fetchUserInfo = async () => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const { data: profile } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single()

    if (profile) {
      setUserInfo(profile)
    }
  }

  const fetchNotifications = async () => {
    try {
      // Fetch notifications from the notifications table
      const { data: dbNotifications, error: notifError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', userInfo.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (notifError) {
        console.error('Error fetching notifications:', notifError)
        return
      }

      // Transform database notifications to UI format
      const formattedNotifications = dbNotifications.map(notif => {
        // Determine the correct link and icon based on notification type
        let link = '/dashboard'
        let iconType = 'default'
        
        switch (notif.type) {
          // User management
          case 'user_approval':
          case 'user_rejection':
            link = userInfo.role === 'admin' ? '/admin/users' : '/dashboard'
            iconType = 'user'
            break
          case 'user_welcome':
            link = '/dashboard'
            iconType = 'welcome'
            break
            
          // Leave requests
          case 'leave_request_pending':
            link = '/leave-request'
            iconType = 'leave'
            break
          case 'leave_request_approved':
          case 'leave_request_rejected':
            link = '/leave-request'
            iconType = notif.type === 'leave_request_approved' ? 'approved' : 'rejected'
            break
            
          // Leave permits
          case 'leave_permit_pending':
            link = '/leave-permit-form'
            iconType = 'permit'
            break
          case 'leave_permit_approved':
          case 'leave_permit_rejected':
            link = '/leave-permit-form'
            iconType = notif.type === 'leave_permit_approved' ? 'approved' : 'rejected'
            break
            
          // Task assignments
          case 'task_assigned':
            link = '/tasks'
            iconType = 'task'
            break
            
          // Attendance
          case 'attendance_submitted':
            link = '/attendance'
            iconType = 'attendance'
            break
            
          // General
          case 'general':
          default:
            link = '/dashboard'
            iconType = 'default'
            break
        }
        
        return {
          id: notif.id,
          type: notif.type,
          title: notif.title,
          message: notif.message,
          link: link,
          iconType: iconType,
          created_at: notif.created_at,
          is_read: notif.is_read,
          related_user_id: notif.related_user_id
        }
      })

      setNotifications(formattedNotifications)
      
      // Count unread notifications
      const unreadNotifications = formattedNotifications.filter(n => !n.is_read)
      setUnreadCount(unreadNotifications.length)
    } catch (error) {
      console.error('Error in fetchNotifications:', error)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Baru saja'
    if (diffInMinutes < 60) return `${diffInMinutes} menit lalu`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} jam lalu`
    return `${Math.floor(diffInMinutes / 1440)} hari lalu`
  }

  const handleOpenDropdown = async () => {
    setIsOpen(!isOpen)
    
    // Mark all notifications as read when opening dropdown
    if (!isOpen && notifications.length > 0) {
      // Update all unread notifications to read in database
      const unreadNotificationIds = notifications
        .filter(n => !n.is_read)
        .map(n => n.id)

      if (unreadNotificationIds.length > 0) {
        const { error } = await supabase
          .from('notifications')
          .update({ 
            is_read: true, 
            read_at: new Date().toISOString() 
          })
          .in('id', unreadNotificationIds)

        if (error) {
          console.error('Error marking notifications as read:', error)
        } else {
          setUnreadCount(0)
          // Refresh to update UI
          fetchNotifications()
        }
      }
    }
  }

  // Don't render if user not logged in
  if (!userInfo) {
    return null
  }

  return (
    <div className="relative">
      {/* Bell Icon Button */}
      <button
        onClick={handleOpenDropdown}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-label="Notifications"
      >
        {unreadCount > 0 ? (
          <BellIconSolid className="w-6 h-6 text-blue-600 animate-bounce" />
        ) : (
          <BellIcon className="w-6 h-6 text-gray-600" />
        )}
        
        {/* Notification Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Panel - Responsive */}
          <div className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto mt-2 sm:w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[80vh] sm:max-h-[500px] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-sm sm:text-lg font-bold text-gray-800">
                  Notifikasi {userInfo.role === 'admin' ? '(Admin)' : ''}
                </h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-red-500 text-white text-[10px] sm:text-xs font-semibold rounded-full">
                    {unreadCount} Baru
                  </span>
                )}
              </div>
            </div>

            {/* Notification List */}
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="px-4 py-6 sm:py-8 text-center">
                  <BellIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-2 sm:mb-3" />
                  <p className="text-gray-500 text-xs sm:text-sm">Tidak ada notifikasi</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => (
                    <Link
                      key={notification.id}
                      to={notification.link}
                      onClick={() => setIsOpen(false)}
                      className="block px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        {/* Icon */}
                        <div className="flex-shrink-0 mt-0.5">
                          {(() => {
                            // Determine icon and color based on iconType
                            let IconComponent = BellIconSolid
                            let bgColor = 'bg-gradient-to-br from-blue-500 to-blue-600'
                            
                            switch (notification.iconType) {
                              case 'user':
                                IconComponent = UserPlusIcon
                                bgColor = 'bg-gradient-to-br from-blue-500 to-blue-600'
                                break
                              case 'welcome':
                                IconComponent = UserCircleIcon
                                bgColor = 'bg-gradient-to-br from-green-500 to-emerald-600'
                                break
                              case 'approved':
                                IconComponent = CheckCircleIcon
                                bgColor = 'bg-gradient-to-br from-green-500 to-green-600'
                                break
                              case 'rejected':
                                IconComponent = XCircleIcon
                                bgColor = 'bg-gradient-to-br from-red-500 to-red-600'
                                break
                              case 'leave':
                              case 'permit':
                                IconComponent = DocumentTextIcon
                                bgColor = 'bg-gradient-to-br from-amber-500 to-orange-600'
                                break
                              case 'task':
                                IconComponent = ClipboardDocumentCheckIcon
                                bgColor = 'bg-gradient-to-br from-blue-500 to-blue-600'
                                break
                              case 'attendance':
                                IconComponent = CalendarDaysIcon
                                bgColor = 'bg-gradient-to-br from-teal-500 to-cyan-600'
                                break
                              default:
                                IconComponent = BellIconSolid
                                bgColor = 'bg-gradient-to-br from-blue-500 to-blue-600'
                            }
                            
                            return (
                              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${bgColor}`}>
                                <IconComponent className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                              </div>
                            )
                          })()}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] sm:text-sm font-semibold text-gray-800 mb-0.5 sm:mb-1 line-clamp-2 leading-tight">
                            {notification.title}
                          </p>
                          <p className="text-[10px] sm:text-sm text-gray-600 mb-0.5 sm:mb-1 line-clamp-2 leading-tight">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-1 text-[9px] sm:text-xs text-gray-500">
                            <span className="truncate">{formatDate(notification.created_at)}</span>
                            {!notification.is_read && (
                              <>
                                <span>•</span>
                                <span className="font-medium text-blue-600 whitespace-nowrap">Baru</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Unread Badge */}
                        {!notification.is_read && (
                          <div className="flex-shrink-0 mt-1">
                            <span className="inline-block w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full"></span>
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-3 sm:px-4 py-2 sm:py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-full text-center text-[11px] sm:text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {userInfo.role === 'admin' ? 'Lihat Semua Permohonan' : 'Lihat Riwayat'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
